import axios from 'axios'
import { StatusCodes } from 'http-status-codes'
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'

import * as dealsAPI from '../../../apis/dealsAPI'
import type {
  InvoiceEntity,
  SendInvoiceReqBody,
} from '../../../apis/dealsAPI'
import { useApiErrorHandler } from '../../../hooks/useApiErrorHandler'
import { useToast } from '../../../hooks/useToast'

import { invoicesQueryKey } from './useInvoicesQuery'

export interface SendInvoiceVariables {
  dealId: string
  invoiceId: string
  body: SendInvoiceReqBody
  /** Generated once per modal instance so retries are idempotent backend-side. */
  idempotencyKey: string
}

interface Snapshot {
  invoicesSnapshot: { key: readonly unknown[]; data: InvoiceEntity[] | undefined }
}

function patchInvoice(
  invoices: InvoiceEntity[],
  invoiceId: string,
  patch: Partial<InvoiceEntity>,
): InvoiceEntity[] {
  return invoices.map((invoice) =>
    invoice.id === invoiceId ? { ...invoice, ...patch } : invoice,
  )
}

export function useSendInvoiceMutation(): UseMutationResult<
  InvoiceEntity,
  unknown,
  SendInvoiceVariables,
  Snapshot
> {
  const queryClient = useQueryClient()
  const toast = useToast()
  const handleError = useApiErrorHandler()

  return useMutation<InvoiceEntity, unknown, SendInvoiceVariables, Snapshot>(
    ({ dealId, invoiceId, body, idempotencyKey }) =>
      dealsAPI.sendInvoice(dealId, invoiceId, body, idempotencyKey),
    {
      onMutate: async ({ dealId, invoiceId, body }): Promise<Snapshot> => {
        await queryClient.cancelQueries({ queryKey: invoicesQueryKey(dealId) })

        const sentAt = new Date().toISOString()
        const optimisticPatch: Partial<InvoiceEntity> = {
          sentAt,
          sentMeta: { to: body.to.trim(), at: sentAt },
        }

        const invoicesKey = invoicesQueryKey(dealId)
        const invoicesData = queryClient.getQueryData<InvoiceEntity[]>(invoicesKey)
        if (invoicesData) {
          queryClient.setQueryData<InvoiceEntity[]>(
            invoicesKey,
            patchInvoice(invoicesData, invoiceId, optimisticPatch),
          )
        }
        return { invoicesSnapshot: { key: invoicesKey, data: invoicesData } }
      },
      onError: (error, { dealId }, context) => {
        if (context) {
          queryClient.setQueryData(context.invoicesSnapshot.key, context.invoicesSnapshot.data)
        }
        if (
          axios.isAxiosError(error) &&
          error.response?.status === StatusCodes.CONFLICT
        ) {
          // 409 covers the in-flight idempotent-send race: re-fetch invoices so
          // the modal's sentAt watcher can pick up the winner's write and
          // auto-close. Generic toast is still shown via handleError.
          queryClient.invalidateQueries({ queryKey: invoicesQueryKey(dealId) })
        }
        handleError(error)
      },
      onSuccess: (invoice, { body }) => {
        toast.current?.show({
          severity: 'success',
          summary: 'Invoice sent',
          detail: `${invoice.invoiceNumber} sent to ${body.to.trim()}`,
          life: 3000,
        })
      },
      onSettled: (_data, _err, { dealId }) => {
        queryClient.invalidateQueries({ queryKey: invoicesQueryKey(dealId) })
      },
    },
  )
}
